import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';

export default function Laboratory() {
  const services = [
    {
      icon: 'ri-test-tube-line',
      title: 'Product Analysis',
      description: 'Comprehensive testing of petroleum products including density, viscosity, flash point, and chemical composition analysis.',
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Quality Monitoring',
      description: 'Continuous monitoring and comparison of baseline specifications against actual product indicators.',
    },
    {
      icon: 'ri-file-list-3-line',
      title: 'Compliance Testing',
      description: 'Verification of products against industry standards and regulatory requirements.',
    },
    {
      icon: 'ri-time-line',
      title: 'Rapid Turnaround',
      description: 'Fast and accurate test results to support timely decision-making and operations.',
    },
    {
      icon: 'ri-bar-chart-box-line',
      title: 'Detailed Reporting',
      description: 'Comprehensive test reports with clear documentation of all findings and recommendations.',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Quality Assurance',
      description: 'Rigorous QA/QC protocols ensuring accuracy and reliability of all test results.',
    },
  ];

  const testParameters = [
    'Density & Specific Gravity',
    'Viscosity (Kinematic & Dynamic)',
    'Flash Point & Fire Point',
    'Pour Point & Cloud Point',
    'Sulfur Content',
    'Water Content',
    'Sediment Analysis',
    'Color & Appearance',
    'Distillation Characteristics',
    'Octane/Cetane Number',
    'Reid Vapor Pressure',
    'Chemical Composition',
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section 
          className="relative bg-cover bg-center h-96 flex items-center"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('https://public.readdy.ai/ai/img_res/33c86b1fc9252d2bc2006a67fed982f6.jpg')`
          }}
        >
          <div className="max-w-7xl mx-auto px-6 lg:px-12 w-full">
            <h1 className="text-5xl font-bold text-white mb-4">Laboratory Analysis</h1>
            <p className="text-xl text-gray-200 max-w-2xl">Precise and timely petroleum product analysis ensuring quality and compliance</p>
          </div>
        </section>

        {/* Overview Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">Professional Quality Control</h2>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  Rigorous quality control is essential in the petroleum industry. Our in-house laboratory provides precise and timely analysis of both incoming and outgoing products, ensuring that every shipment meets the highest quality standards.
                </p>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  Through continuous monitoring, we compare baseline specifications against actual product indicators, delivering critical quality assessments that inform decision-making and ensure compliance with industry standards and regulations.
                </p>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Our experienced laboratory technicians utilize state-of-the-art analytical equipment and follow internationally recognized testing methods to provide accurate, reliable results you can trust.
                </p>
              </div>
              <div>
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20petroleum%20laboratory%20technician%20in%20white%20coat%20and%20safety%20goggles%20examining%20petroleum%20sample%20with%20advanced%20testing%20equipment%2C%20modern%20laboratory%20interior%20with%20analytical%20instruments%20and%20computer%20displays%2C%20scientist%20conducting%20precise%20quality%20control%20tests%20on%20petroleum%20products%2C%20high-tech%20laboratory%20environment%20with%20professional%20lighting%20and%20clean%20organized%20workspace%2C%20petroleum%20quality%20analysis%20with%20modern%20scientific%20equipment&width=600&height=700&seq=lab002&orientation=portrait"
                  alt="Laboratory analysis"
                  className="rounded-2xl shadow-xl w-full h-auto object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Laboratory Services</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Comprehensive testing and analysis services for all petroleum product types
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <div key={index} className="bg-white rounded-xl p-8 shadow-sm hover:shadow-lg transition-shadow">
                  <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                    <i className={`${service.icon} text-2xl text-blue-600`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{service.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{service.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Test Parameters */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Test Parameters</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                We conduct comprehensive testing across all critical petroleum product parameters
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {testParameters.map((parameter, index) => (
                <div key={index} className="flex items-center gap-3 bg-gray-50 rounded-lg p-4">
                  <i className="ri-check-line text-blue-600 text-xl"></i>
                  <span className="text-gray-700 font-medium">{parameter}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Our Testing Process</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">1</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Sample Collection</h3>
                <p className="text-gray-600">Representative samples collected following strict protocols</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">2</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Laboratory Testing</h3>
                <p className="text-gray-600">Comprehensive analysis using advanced equipment</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">3</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Quality Review</h3>
                <p className="text-gray-600">Results verified against specifications and standards</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">4</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Report Delivery</h3>
                <p className="text-gray-600">Detailed reports provided with recommendations</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-blue-600">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
            <h2 className="text-4xl font-bold text-white mb-6">Need Quality Analysis?</h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Contact us to discuss your petroleum product testing requirements
            </p>
            <Link 
              to="/contact" 
              className="inline-block px-8 py-4 bg-white text-blue-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              Request Analysis
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}